import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getAuthConfig } from '../utils/auth';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Package, Check, Truck, TrendingDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1500);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [memberPrice, setMemberPrice] = useState(0);
  const [zipCode, setZipCode] = useState('');
  const [freightEstimate, setFreightEstimate] = useState(null);
  const [calculatingFreight, setCalculatingFreight] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/products/${productId}`);
        setProduct(response.data);
        calculatePrice(quantity, response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  const calculatePrice = (qty, prod = product) => {
    if (!prod) return;
    
    let price = prod.base_price;
    let memPrice = prod.member_base_price;
    
    for (let i = prod.volume_pricing.length - 1; i >= 0; i--) {
      if (qty >= prod.volume_pricing[i].min_sqft) {
        price = prod.volume_pricing[i].price;
        break;
      }
    }
    
    for (let i = prod.member_volume_pricing.length - 1; i >= 0; i--) {
      if (qty >= prod.member_volume_pricing[i].min_sqft) {
        memPrice = prod.member_volume_pricing[i].price;
        break;
      }
    }
    
    setCurrentPrice(price);
    setMemberPrice(memPrice);
  };
  
  const calculateFreight = async () => {
    if (!zipCode || zipCode.length < 5) {
      toast.error('Please enter a valid ZIP code');
      return;
    }
    
    setCalculatingFreight(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/freight/estimate`, {
        quantity_sqft: quantity,
        destination_zip: zipCode
      });
      setFreightEstimate(response.data);
      toast.success('Freight estimate calculated!');
    } catch (error) {
      toast.error('Failed to calculate freight');
    } finally {
      setCalculatingFreight(false);
    }
  };

  const handleQuantityChange = (e) => {
    const qty = parseInt(e.target.value) || 0;
    setQuantity(qty);
    calculatePrice(qty);
  };

  const handleAddToOrder = async () => {
    try {
      await axios.get(`${BACKEND_URL}/api/auth/me`, getAuthConfig());
      
      const container = await axios.get(`${BACKEND_URL}/api/containers/active`);
      
      await axios.post(
        `${BACKEND_URL}/api/orders`,
        {
          product_id: productId,
          quantity: quantity,
          container_id: container.data.container_id
        },
        getAuthConfig()
      );
      
      toast.success('Order added successfully!');
      navigate('/dashboard');
    } catch (error) {
      if (error.response?.status === 401) {
        const redirectUrl = window.location.origin + '/dashboard';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      } else {
        toast.error('Failed to add order');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 px-4 text-center">
          <p className="text-muted-foreground">Product not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8" data-testid="product-detail-page">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square overflow-hidden rounded-sm border border-border">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
            </div>

            <div>
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-medium uppercase tracking-wider rounded-sm">
                  {product.category}
                </span>
              </div>
              
              <h1 className="font-chivo font-black text-4xl md:text-5xl mb-4" data-testid="product-detail-name">
                {product.name}
              </h1>
              
              <p className="font-manrope text-base md:text-lg text-muted-foreground mb-8">
                {product.description}
              </p>

              <div className="mb-8 p-6 bg-muted rounded-sm">
                <h3 className="font-chivo font-semibold text-xl mb-4">Specifications</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key}>
                      <div className="text-muted-foreground capitalize">{key.replace('_', ' ')}</div>
                      <div className="font-medium">{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-8 p-6 border border-primary/20 bg-primary/5 rounded-sm">
                <h3 className="font-chivo font-semibold text-xl mb-4">Volume Pricing</h3>
                <div className="space-y-2">
                  {product.volume_pricing.map((tier, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <div className="flex items-center">
                        <Check className="w-4 h-4 text-primary mr-2" />
                        <span>{tier.min_sqft}+ sqft</span>
                      </div>
                      <span className="font-chivo font-bold text-primary">${tier.price.toFixed(2)}/sqft</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block font-manrope font-medium text-sm uppercase tracking-widest text-muted-foreground mb-2">
                  Quantity (sqft)
                </label>
                <Input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={handleQuantityChange}
                  className="h-12 text-lg"
                  data-testid="quantity-input"
                />
              </div>

              <div className="mb-6 p-6 bg-card border border-border rounded-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">Price per sqft:</span>
                  <span className="font-chivo font-bold text-xl">${currentPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center mb-4 pb-4 border-b border-border">
                  <span className="font-chivo font-semibold text-lg">Subtotal:</span>
                  <span className="font-chivo font-bold text-3xl text-primary" data-testid="total-price">
                    ${(currentPrice * quantity).toFixed(2)}
                  </span>
                </div>
                
                {memberPrice < currentPrice && (
                  <div className="bg-accent/10 border border-accent/20 rounded-sm p-4">
                    <div className="flex items-center mb-2">
                      <TrendingDown className="w-5 h-5 text-accent mr-2" />
                      <span className="font-chivo font-semibold text-accent">Member Savings Available!</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm">Member Price:</span>
                      <span className="font-chivo font-bold text-lg text-accent">${memberPrice.toFixed(2)}/sqft</span>
                    </div>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-semibold">You Save:</span>
                      <span className="font-chivo font-bold text-xl text-accent">
                        ${((currentPrice - memberPrice) * quantity).toFixed(2)}
                      </span>
                    </div>
                    <Link to="/membership">
                      <Button variant="outline" className="w-full border-accent text-accent hover:bg-accent hover:text-white" size="sm">
                        View Membership Plans
                      </Button>
                    </Link>
                  </div>
                )}
              </div>

              <div className="mb-6 p-6 bg-muted rounded-sm">
                <div className="flex items-center mb-4">
                  <Truck className="w-5 h-5 text-primary mr-2" />
                  <h3 className="font-chivo font-semibold text-lg">Freight Estimate</h3>
                </div>
                <div className="flex gap-3 mb-4">
                  <Input
                    type="text"
                    placeholder="ZIP Code"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    maxLength={5}
                    className="flex-1"
                    data-testid="zip-code-input"
                  />
                  <Button
                    onClick={calculateFreight}
                    disabled={calculatingFreight}
                    variant="outline"
                    data-testid="calculate-freight-button"
                  >
                    {calculatingFreight ? 'Calculating...' : 'Calculate'}
                  </Button>
                </div>
                
                {freightEstimate && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Base Freight:</span>
                      <span className="font-medium">${freightEstimate.base_freight_cost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Per Sqft ({quantity} sqft):</span>
                      <span className="font-medium">${(freightEstimate.per_sqft_cost * quantity).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-border">
                      <span className="font-chivo font-semibold">Total Freight:</span>
                      <span className="font-chivo font-bold text-lg text-primary">
                        ${freightEstimate.total_freight_cost.toFixed(2)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      Estimated delivery: {freightEstimate.estimated_delivery_days} days
                    </div>
                  </div>
                )}
              </div>

              <Button
                onClick={handleAddToOrder}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground text-lg font-medium"
                data-testid="add-to-order-button"
              >
                <Package className="mr-2 w-5 h-5" />
                Add to Order
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
